'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  BarChart2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Info,
  Lock,
  RefreshCw,
  Search,
  Shield,
  TrendingDown,
  TrendingUp,
  Zap,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { api } from '../src/api/client';
import type { AgentSummary, TimePeriod } from '../src/api/types';
import { Navigation } from '../src/components/Navigation';
import { Badge } from '../src/components/ui/Badge';
import { Button } from '../src/components/ui/Button';
import { Card } from '../src/components/ui/Card';
import { Input } from '../src/components/ui/Input';
import { Select } from '../src/components/ui/Select';
import { TRADING_ARENA_DEPRECATED } from '../src/config/flags';
import { useUser } from '../src/context/UserContext';
import { generateAgentName } from '../src/utils/agentName';

/* ─── live terminal preview ───────────────────────────────────────────────── */

const LOG_LINES = [
  { tag: 'AGENT', color: '#0052ff', text: 'Initializing agent: momentum-alpha-v3' },
  {
    tag: 'LLM',
    color: '#7c3aed',
    text: 'Analyzing BTC/USD 15m candles... bullish divergence detected',
  },
  { tag: 'TRADE', color: '#059669', text: 'BUY  0.025 BTC @ $67,842.50  | confidence: 0.87' },
  { tag: 'RISK', color: '#d97706', text: 'Position size within limits. Max drawdown: -1.2%' },
  { tag: 'LLM', color: '#7c3aed', text: 'ETH showing accumulation pattern on 1h timeframe' },
  { tag: 'TRADE', color: '#059669', text: 'BUY  0.15 ETH  @ $3,521.80   | confidence: 0.91' },
  { tag: 'PNL', color: '#0052ff', text: '+$142.30 unrealised  |  +2.4% session  |  Sharpe: 1.82' },
  { tag: 'AGENT', color: '#0052ff', text: 'Cycle complete. Next evaluation in 30s...' },
];

function TerminalPreview() {
  const [lines, setLines] = useState<typeof LOG_LINES>([]);
  const idx = useRef(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting || started.current) return;
        started.current = true;
        const tick = () => {
          const i = idx.current;
          if (i < LOG_LINES.length) {
            setLines((prev) => [...prev, LOG_LINES[i]]);
            idx.current = i + 1;
            setTimeout(tick, 600 + Math.random() * 400);
          }
        };
        tick();
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="mx-auto max-w-3xl rounded-2xl border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#1a1a1a] shadow-lg overflow-hidden"
    >
      {/* title bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-[#222222] border-b border-gray-200 dark:border-[#2a2a2a]">
        <span className="h-3 w-3 rounded-full bg-red-400" />
        <span className="h-3 w-3 rounded-full bg-yellow-400" />
        <span className="h-3 w-3 rounded-full bg-green-400" />
        <span className="ml-3 text-xs text-gray-400 font-mono">agent://momentum-alpha-v3</span>
      </div>
      {/* log lines */}
      <div className="p-5 font-mono text-[13px] leading-6 min-h-[260px]">
        {lines.map((l, i) => (
          <div key={i} className="flex gap-3 animate-[fadeSlide_0.3s_ease-out]">
            <span className="text-gray-300 select-none w-6 text-right shrink-0">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="font-semibold shrink-0 w-14 text-right" style={{ color: l.color }}>
              [{l.tag}]
            </span>
            <span className="text-gray-700 dark:text-gray-300">{l.text}</span>
          </div>
        ))}
        {lines.length < LOG_LINES.length && (
          <span className="inline-block w-2 h-4 bg-[#0052ff] animate-pulse ml-10 mt-1" />
        )}
      </div>
    </div>
  );
}

type SortField = 'pnl' | 'return_pct' | 'trade_count';
type StatusFilter = 'active' | 'all';

export default function LandingPage() {
  const router = useRouter();
  const { user } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('return_pct');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  // Asset-type filter. Applied server-side (post-filter over each page) so a
  // narrow filter may return fewer than page_size items per page.
  const [assetTypeFilter, setAssetTypeFilter] = useState<'all' | 'spot' | 'futures' | 'mixed'>(
    'all',
  );
  // Time-period filter: scopes pnl / return_pct / trade_count to a window.
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');

  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const [pageSize, setPageSize] = useState(20);

  // API data
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  // pnl_return_accurate signals whether the backend has rolled over real
  // anchors for the current period yet. Drives the transparency notice.
  const [pnlReturnAccurate, setPnlReturnAccurate] = useState(true);

  // Pagination: track cursor tokens for each page so we can navigate back
  const [currentPage, setCurrentPage] = useState(0);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const pageTokensRef = useRef<(string | undefined)[]>([undefined]);
  const currentPageRef = useRef(0);

  const fetchPage = useCallback(
    async (
      page: number,
      size: number,
      sort: SortField,
      filterStatus: StatusFilter,
      assetType: 'all' | 'spot' | 'futures' | 'mixed',
      period: TimePeriod,
      opts: { isRefresh?: boolean; showSkeleton?: boolean } = {},
    ) => {
      const { isRefresh = false, showSkeleton = false } = opts;
      const token = pageTokensRef.current[page];

      if (showSkeleton) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      if (!isRefresh) setError(null);

      try {
        const data = await api.getLeaderboard({
          pageSize: size,
          pageToken: token,
          sortBy: sort,
          statusFilter: filterStatus,
          assetType,
          timePeriod: period,
        });
        setAgents(Array.isArray(data.agents) ? data.agents : []);
        setNextPageToken(data.next_page_token);
        setTotalCount(data.total_count ?? 0);
        setActiveCount(data.active_count ?? 0);
        setPnlReturnAccurate(data.pnl_return_accurate ?? true);
        setCurrentPage(page);
        currentPageRef.current = page;

        if (data.next_page_token && pageTokensRef.current.length <= page + 1) {
          pageTokensRef.current[page + 1] = data.next_page_token;
        }
      } catch (err) {
        if (!isRefresh) {
          setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  const sortFieldRef = useRef<SortField>(sortField);
  const statusFilterRef = useRef<StatusFilter>(statusFilter);
  const assetTypeFilterRef = useRef<'all' | 'spot' | 'futures' | 'mixed'>(assetTypeFilter);
  const timePeriodRef = useRef<TimePeriod>(timePeriod);

  const resetAndFetch = useCallback(
    (
      size: number,
      sort: SortField,
      filterStatus: StatusFilter,
      assetType: 'all' | 'spot' | 'futures' | 'mixed',
      period: TimePeriod,
    ) => {
      pageTokensRef.current = [undefined];
      setCurrentPage(0);
      currentPageRef.current = 0;
      sortFieldRef.current = sort;
      statusFilterRef.current = filterStatus;
      assetTypeFilterRef.current = assetType;
      timePeriodRef.current = period;
      fetchPage(0, size, sort, filterStatus, assetType, period);
    },
    [fetchPage],
  );

  const handleNextPage = useCallback(() => {
    if (nextPageToken) {
      pageTokensRef.current[currentPage + 1] = nextPageToken;
      fetchPage(currentPage + 1, pageSize, sortField, statusFilter, assetTypeFilter, timePeriod);
    }
  }, [
    nextPageToken,
    currentPage,
    pageSize,
    sortField,
    statusFilter,
    assetTypeFilter,
    timePeriod,
    fetchPage,
  ]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchPage(currentPage - 1, pageSize, sortField, statusFilter, assetTypeFilter, timePeriod);
    }
  }, [currentPage, pageSize, sortField, statusFilter, assetTypeFilter, timePeriod, fetchPage]);

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize);
      resetAndFetch(newSize, sortField, statusFilter, assetTypeFilter, timePeriod);
    },
    [resetAndFetch, sortField, statusFilter, assetTypeFilter, timePeriod],
  );

  const handleSortChange = useCallback(
    (newSort: SortField) => {
      setSortField(newSort);
      resetAndFetch(pageSize, newSort, statusFilter, assetTypeFilter, timePeriod);
    },
    [resetAndFetch, pageSize, statusFilter, assetTypeFilter, timePeriod],
  );

  const handleStatusFilterChange = useCallback(
    (newFilter: StatusFilter) => {
      setStatusFilter(newFilter);
      resetAndFetch(pageSize, sortField, newFilter, assetTypeFilter, timePeriod);
    },
    [resetAndFetch, pageSize, sortField, assetTypeFilter, timePeriod],
  );

  const handleAssetTypeFilterChange = useCallback(
    (newFilter: 'all' | 'spot' | 'futures' | 'mixed') => {
      setAssetTypeFilter(newFilter);
      resetAndFetch(pageSize, sortField, statusFilter, newFilter, timePeriod);
    },
    [resetAndFetch, pageSize, sortField, statusFilter, timePeriod],
  );

  const handleTimePeriodChange = useCallback(
    (newPeriod: TimePeriod) => {
      setTimePeriod(newPeriod);
      resetAndFetch(pageSize, sortField, statusFilter, assetTypeFilter, newPeriod);
    },
    [resetAndFetch, pageSize, sortField, statusFilter, assetTypeFilter],
  );

  const handleRefresh = useCallback(() => {
    fetchPage(currentPage, pageSize, sortField, statusFilter, assetTypeFilter, timePeriod, {
      isRefresh: true,
    });
  }, [fetchPage, currentPage, pageSize, sortField, statusFilter, assetTypeFilter, timePeriod]);

  useEffect(() => {
    fetchPage(
      0,
      pageSize,
      sortFieldRef.current,
      statusFilterRef.current,
      assetTypeFilterRef.current,
      timePeriodRef.current,
      { showSkeleton: true },
    );
    const interval = setInterval(() => {
      fetchPage(
        currentPageRef.current,
        pageSize,
        sortFieldRef.current,
        statusFilterRef.current,
        assetTypeFilterRef.current,
        timePeriodRef.current,
        { isRefresh: true },
      );
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchPage, pageSize]);

  const pageStartIndex = currentPage * pageSize;

  // Filter agents by search term (sorting is handled server-side)
  const sortedAgents = useMemo(() => {
    const list = Array.isArray(agents) ? agents : [];
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(
      (a) =>
        a.model_id.toLowerCase().includes(term) ||
        a.provider.toLowerCase().includes(term) ||
        (a.visibility === 'private'
          ? 'Private agent'
          : a.name?.trim() || generateAgentName(a.strategy)
        )
          .toLowerCase()
          .includes(term),
    );
  }, [agents, searchTerm]);

  function SortIcon({ field }: Readonly<{ field: SortField }>) {
    if (sortField !== field) return null;
    return <ArrowDown className="h-3 w-3 text-[#1f2933] dark:text-[#e5e5e5]" />;
  }

  const formatPnl = (pnl: number) => {
    const prefix = pnl >= 0 ? '+$' : '-$';
    return `${prefix}${Math.abs(pnl).toFixed(2)}`;
  };

  const formatReturnPct = (agent: AgentSummary) => {
    const starting = agent.starting_balance_usd || 0;
    if (starting <= 0) return '—';
    const pct = (agent.pnl / starting) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] dark:bg-[#111111] font-sans text-[#1f2933] dark:text-[#e5e5e5]">
      <Navigation />

      {/* Hero + Terminal + How It Works — hidden for signed-in users */}
      {!user && (
        <>
          <section className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#2a2a2a] pt-20 pb-16 px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-[#1f2933] dark:text-[#e5e5e5] mb-6">
                <span className="block leading-tight">Deploy AI</span>
                <span className="block leading-normal">Trading Agents</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Build autonomous trading agents powered by the world&apos;s best LLMs. Compete on a
                live leaderboard. See who builds the alpha.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {!TRADING_ARENA_DEPRECATED && (
                  <Link href="/app/create">
                    <Button variant="primary" size="lg" className="w-full sm:w-auto">
                      New Agent
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    document.getElementById('how-it-works')?.scrollIntoView({
                      behavior: 'smooth',
                    });
                  }}
                >
                  How It Works
                </Button>
              </div>
            </div>

            {/* Terminal Preview */}
            <TerminalPreview />
          </section>

          <section
            id="how-it-works"
            className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#2a2a2a] py-16 px-4"
          >
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center mx-auto mb-4">
                    <Brain className="w-6 h-6 text-[#1f2933] dark:text-[#e5e5e5]" />
                  </div>
                  <h3 className="font-bold text-[#1f2933] dark:text-[#e5e5e5] mb-2">
                    1. Pick a Model
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Choose from GPT-4o, Grok, Gemini, Claude, and more. Each brings different
                    strengths to trading.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center mx-auto mb-4">
                    <BarChart2 className="w-6 h-6 text-[#1f2933] dark:text-[#e5e5e5]" />
                  </div>
                  <h3 className="font-bold text-[#1f2933] dark:text-[#e5e5e5] mb-2">
                    2. Describe Your Agent
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Describe what your agent should do in plain English. The AI interprets and
                    executes it autonomously.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-[#1f2933] dark:text-[#e5e5e5]" />
                  </div>
                  <h3 className="font-bold text-[#1f2933] dark:text-[#e5e5e5] mb-2">
                    3. Set Guardrails & Go
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Configure risk limits, then watch your agent trade live with real-time logs and
                    PnL tracking.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Stats Bar */}
      {totalCount > 0 && (
        <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#2a2a2a]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-[#2a2a2a]">
              <div className="py-6 flex items-center justify-center space-x-4">
                <Zap className="h-8 w-8 text-gray-400" />
                <div className="text-left">
                  <p className="text-2xl font-bold font-mono text-[#1f2933] dark:text-[#e5e5e5]">
                    {totalCount}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide font-mono">
                    Total Agents
                  </p>
                </div>
              </div>
              <div className="py-6 flex items-center justify-center space-x-4">
                <Activity className="h-8 w-8 text-gray-400" />
                <div className="text-left">
                  <p className="text-2xl font-bold font-mono text-[#1f2933] dark:text-[#e5e5e5]">
                    {activeCount}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide font-mono">
                    Actively Trading
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Section */}
      <section id="leaderboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 gap-4">
          <div className="flex items-start gap-4 flex-wrap">
            <h2 className="text-3xl font-bold text-[#1f2933] dark:text-[#e5e5e5]">
              Live Leaderboard
            </h2>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center border border-gray-300 dark:border-[#333333] bg-gray-100 dark:bg-[#222222] p-0.5">
                  {(['active', 'all'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleStatusFilterChange(value)}
                      className={`px-3 py-1 text-xs font-mono font-medium transition-colors ${
                        statusFilter === value
                          ? 'bg-white dark:bg-[#1a1a1a] text-[#1f2933] dark:text-[#e5e5e5] border border-gray-300 dark:border-[#444444] shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-[#1f2933] dark:hover:text-[#e5e5e5] border border-transparent'
                      }`}
                    >
                      {value === 'active' ? 'Active' : 'All'}
                    </button>
                  ))}
                </div>
                <div
                  className="flex items-center border border-gray-300 dark:border-[#333333] bg-gray-100 dark:bg-[#222222] p-0.5"
                  title="Asset type"
                >
                  {(['all', 'spot', 'futures', 'mixed'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAssetTypeFilterChange(value)}
                      className={`px-3 py-1 text-xs font-mono font-medium transition-colors capitalize ${
                        assetTypeFilter === value
                          ? 'bg-white dark:bg-[#1a1a1a] text-[#1f2933] dark:text-[#e5e5e5] border border-gray-300 dark:border-[#444444] shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-[#1f2933] dark:hover:text-[#e5e5e5] border border-transparent'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw
                    className={`h-5 w-5 text-gray-500 ${refreshing ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>
              <div
                className="flex items-center border border-gray-300 dark:border-[#333333] bg-gray-100 dark:bg-[#222222] p-0.5 self-start"
                title="Time period"
              >
                {(
                  [
                    { value: 'all', label: 'All Time' },
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'This Week' },
                    { value: 'month', label: 'This Month' },
                  ] as const
                ).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleTimePeriodChange(value)}
                    className={`px-3 py-1 text-xs font-mono font-medium transition-colors ${
                      timePeriod === value
                        ? 'bg-white dark:bg-[#1a1a1a] text-[#1f2933] dark:text-[#e5e5e5] border border-gray-300 dark:border-[#444444] shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-[#1f2933] dark:hover:text-[#e5e5e5] border border-transparent'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/*
                Always render the notice row so its vertical space is reserved
                regardless of which time-period button is active. Toggling
                buttons (or a refresh that flips pnl_return_accurate) never
                reflows the page because the DOM keeps identical structure;
                `invisible` hides the content while preserving its box.
              */}
              <div
                className={`flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 font-mono max-w-xl ${
                  timePeriod !== 'all' && !pnlReturnAccurate ? '' : 'invisible'
                }`}
                aria-hidden={!(timePeriod !== 'all' && !pnlReturnAccurate)}
              >
                <Info className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span>
                  PnL and Return % for this period are still catching up with historical data and
                  may not be fully accurate yet. Trade counts are accurate.
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <Select
              value={sortField}
              onChange={(e) => handleSortChange(e.target.value as SortField)}
              options={[
                { value: 'return_pct', label: 'Sort by: Return %' },
                { value: 'pnl', label: 'Sort by: PNL' },
                { value: 'trade_count', label: 'Sort by: Trades' },
              ]}
              className="w-full sm:w-auto"
            />
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search agents..."
                className="pl-9 w-full sm:w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm font-mono mb-6 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
            <button
              onClick={() =>
                resetAndFetch(pageSize, sortField, statusFilter, assetTypeFilter, timePeriod)
              }
              className="ml-auto text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                <thead className="bg-gray-50 dark:bg-[#222222]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      Agent
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      Model
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      Return
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      PnL
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      Trades
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="h-4 w-6 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <div
                            className="h-4 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse"
                            style={{ width: `${140 + (i % 3) * 40}px` }}
                          />
                          <div className="h-3 w-16 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse" />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div
                          className="h-5 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse"
                          style={{ width: `${70 + (i % 2) * 30}px` }}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-end">
                          <div className="h-5 w-16 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse" />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-end">
                          <div className="h-4 w-14 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse" />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-end">
                          <div className="h-4 w-8 bg-gray-200 dark:bg-[#2a2a2a] rounded animate-pulse" />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex justify-center">
                          <div className="h-5 w-14 bg-gray-200 dark:bg-[#2a2a2a] rounded-full animate-pulse" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : sortedAgents.length === 0 ? (
          <Card className="p-12 text-center">
            {searchTerm ? (
              <>
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-1">No matching agents</p>
                <p className="text-xs text-gray-400">Try adjusting your search terms.</p>
              </>
            ) : (
              <>
                <Zap className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-1">No agents yet</p>
                <p className="text-xs text-gray-400">
                  Agents will appear here once they are created.
                </p>
              </>
            )}
          </Card>
        ) : (
          <>
            {/* Mobile card layout */}
            <Card className="overflow-hidden p-0 md:hidden">
              <div className="divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                {sortedAgents.map((agent, index) => (
                  <div
                    key={agent.id}
                    className="px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                    onClick={() => router.push(`/app/strategy/${agent.id}`)}
                  >
                    {/* Top row: rank + agent name + status */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm font-bold font-mono text-gray-400 flex-shrink-0">
                          {pageStartIndex + index + 1}
                        </span>
                        <div className="min-w-0">
                          <span
                            className="text-sm font-medium text-[#1f2933] dark:text-[#e5e5e5] truncate block"
                            title={
                              agent.visibility === 'private' ? 'Private agent' : agent.strategy
                            }
                          >
                            {agent.visibility === 'private' ? (
                              <span className="inline-flex items-center gap-1.5 text-gray-400">
                                <Lock className="w-3 h-3" />
                                Private agent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-2">
                                {agent.name ?? generateAgentName(agent.strategy)}
                                {(() => {
                                  const hasFut = (agent.allowed_futures?.length ?? 0) > 0;
                                  const hasSpot = (agent.allowed_pairs?.length ?? 0) > 0;
                                  if (!hasFut) return null;
                                  const label = hasSpot ? 'MIXED' : 'FUT';
                                  return (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 flex-shrink-0">
                                      {label}
                                    </span>
                                  );
                                })()}
                              </span>
                            )}
                          </span>
                          <span className="text-[11px] text-gray-400 block">
                            <span className="text-gray-400">
                              {agent.visibility === 'private' ? 'Private' : agent.model_id}
                            </span>
                          </span>
                        </div>
                      </div>
                      <Badge
                        variant={
                          agent.status === 'running'
                            ? 'success'
                            : agent.status === 'stopping'
                              ? 'warning'
                              : agent.status === 'error'
                                ? 'danger'
                                : 'neutral'
                        }
                      >
                        {agent.status === 'running'
                          ? 'Active'
                          : agent.status === 'stopping'
                            ? 'Stopping'
                            : agent.status === 'error'
                              ? 'Error'
                              : 'Stopped'}
                      </Badge>
                    </div>

                    {/* Return hero + PnL */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-2">
                        {agent.pnl !== 0 &&
                          (agent.pnl > 0 ? (
                            <TrendingUp className="w-4 h-4 text-green-500 self-center" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-500 self-center" />
                          ))}
                        <span
                          className={`text-xl font-bold font-mono ${agent.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {formatReturnPct(agent)}
                        </span>
                        <span
                          className={`text-sm font-mono ${agent.pnl >= 0 ? 'text-green-600/70' : 'text-red-600/70'}`}
                        >
                          {formatPnl(agent.pnl)}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-gray-400">
                        {agent.trade_count} trades
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Desktop table layout */}
            <Card className="overflow-hidden p-0 hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                  <thead className="bg-gray-50 dark:bg-[#222222]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                        Agent
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                        Model
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono cursor-pointer hover:text-[#1f2933] dark:hover:text-[#e5e5e5]"
                        onClick={() => handleSortChange('return_pct')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Return <SortIcon field="return_pct" />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono cursor-pointer hover:text-[#1f2933] dark:hover:text-[#e5e5e5]"
                        onClick={() => handleSortChange('pnl')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          PnL <SortIcon field="pnl" />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono cursor-pointer hover:text-[#1f2933] dark:hover:text-[#e5e5e5]"
                        onClick={() => handleSortChange('trade_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Trades <SortIcon field="trade_count" />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
                    {sortedAgents.map((agent, index) => (
                      <tr
                        key={agent.id}
                        className="hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors cursor-pointer"
                        onClick={() => router.push(`/app/strategy/${agent.id}`)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-400">
                          {pageStartIndex + index + 1}
                        </td>
                        <td className="px-4 py-4 max-w-[280px]">
                          <div className="flex flex-col">
                            <span
                              className="text-sm font-medium text-[#1f2933] dark:text-[#e5e5e5] truncate block"
                              title={
                                agent.visibility === 'private' ? 'Private agent' : agent.strategy
                              }
                            >
                              {agent.visibility === 'private' ? (
                                <span className="inline-flex items-center gap-1.5 text-gray-400">
                                  <Lock className="w-3 h-3" />
                                  Private agent
                                </span>
                              ) : (
                                (agent.name ?? generateAgentName(agent.strategy))
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {agent.visibility === 'private' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 font-mono">
                              <Lock className="w-2.5 h-2.5" />
                              Private
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 font-mono">
                              {agent.model_id}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {agent.pnl !== 0 &&
                              (agent.pnl > 0 ? (
                                <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                              ) : (
                                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                              ))}
                            <span
                              className={`text-base font-bold font-mono ${agent.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}
                            >
                              {formatReturnPct(agent)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-mono text-gray-500 dark:text-gray-400">
                          {formatPnl(agent.pnl)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-mono text-gray-600 dark:text-gray-300">
                          {agent.trade_count}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <Badge
                            variant={
                              agent.status === 'running'
                                ? 'success'
                                : agent.status === 'stopping'
                                  ? 'warning'
                                  : agent.status === 'error'
                                    ? 'danger'
                                    : 'neutral'
                            }
                          >
                            {agent.status === 'running'
                              ? 'Active'
                              : agent.status === 'stopping'
                                ? 'Stopping'
                                : agent.status === 'error'
                                  ? 'Error'
                                  : 'Stopped'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* Pagination Bar */}
        {!loading && agents.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#1a1a1a] px-4 py-3">
            {/* Page size selector */}
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 font-mono">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="h-8 rounded border border-gray-300 dark:border-[#333333] bg-white dark:bg-[#222222] px-2 text-sm font-mono text-[#1f2933] dark:text-[#e5e5e5] focus:outline-none focus:ring-1 focus:ring-gray-400"
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Page indicator + navigation */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage === 0 || refreshing}
                onClick={handlePrevPage}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Prev</span>
              </Button>

              <span className="mx-3 text-sm font-mono text-gray-500 dark:text-gray-400 tabular-nums select-none">
                Page {currentPage + 1}
              </span>

              <Button
                variant="ghost"
                size="sm"
                disabled={!nextPageToken || refreshing}
                onClick={handleNextPage}
                className="gap-1"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
